import React from "react";
import clsx from "clsx";

interface IFormSection {
  /** Micro-label heading for the group of fields. */
  title: string;
  /** Optional one-line clarification shown under the title. */
  description?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * A titled block of form fields. Two columns from `sm` up, one below —
 * the grid does the aligning, so fields never need width overrides.
 */
export const FormSection: React.FC<IFormSection> = ({
  title,
  description,
  children,
  className,
}) => (
  <section
    className={clsx(
      "rounded-xl border border-edge-faint bg-overlay px-5 py-5 sm:px-6",
      className
    )}
  >
    <header className="mb-5 flex flex-col gap-1">
      <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-light-gray">
        {title}
      </h3>
      {description ? (
        <p className="text-[12px] font-medium text-text-secondary">
          {description}
        </p>
      ) : null}
    </header>
    <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
      {children}
    </div>
  </section>
);

/** Wraps a single control; `span` makes it take the full row. */
export const FormField: React.FC<{
  span?: 1 | 2;
  children: React.ReactNode;
}> = ({ span = 1, children }) => (
  <div className={clsx("min-w-0", span === 2 && "sm:col-span-2")}>
    {children}
  </div>
);

/** Label + inline choices, matching the height rhythm of a text field. */
export const RadioField: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <fieldset className="min-w-0">
    <legend className="mb-2 block text-[12px] font-medium text-label-muted">
      {label}
    </legend>
    <div className="flex h-[40px] flex-wrap items-center gap-x-5 gap-y-2">
      {children}
    </div>
  </fieldset>
);

export default FormSection;
